import React, { Fragment, useMemo } from "react";

import {
  formatCurrency,
  formatDate,
  formatPercent,
  DateFormat,
} from "../../../../../utils";
import { getTermInfo } from "../LiabilityHero/helpers";
import {
  buildBreakdownStats,
  getRateSubLabel,
  getRepaidPercent,
} from "./helpers";
import { BreakdownDetail, LoanBreakdownProps } from "./types";
import {
  BreakdownCard,
  BreakdownHeader,
  BreakdownNote,
  BreakdownTitle,
  DetailItem,
  DetailItemLabel,
  DetailItemSub,
  DetailItemValue,
  DetailRow,
  DetailsDivider,
  Dot,
  NotePrefix,
  ProgressFill,
  ProgressTrack,
  RepaidTag,
  StatColumn,
  StatColumnLabel,
  StatColumnValue,
  StatColumnValueRow,
  StatColumns,
  StatDivider,
  StatList,
  StatRowItem,
  StatRowLabel,
  StatRowLeft,
  StatRowValue,
  Swatch,
} from "./styles";
import { currentRateOf } from "../../../helpers";

const DIVIDER_BEFORE_KEY = "interest";

export const LoanBreakdown: React.FC<LoanBreakdownProps> = React.memo(
  ({ liability }) => {
    const stats = useMemo(() => buildBreakdownStats(liability), [liability]);
    const repaidPercent = useMemo(
      () => getRepaidPercent(liability),
      [liability],
    );

    const details = useMemo<BreakdownDetail[]>(() => {
      const currentRate = currentRateOf(liability.interestRateHistory);
      const interestRate = currentRate?.interestRate ?? 0;
      const termInfo = getTermInfo(liability.startDate, liability.endDate);

      const items: BreakdownDetail[] = [
        {
          key: "type",
          label: "Liability type",
          value: liability.liabilityTypeName,
          testId: "breakdown-liability-type",
        },
        {
          key: "institution",
          label: "Institution",
          value: liability.institution ?? "Not specified",
          testId: "breakdown-institution",
        },
        {
          key: "rate",
          label: "Interest rate",
          value: `${interestRate.toFixed(3)}%`,
          sub: getRateSubLabel(currentRate),
          accent: true,
          testId: "breakdown-interest-rate",
        },
        {
          key: "start",
          label: "Start date",
          value: formatDate(liability.startDate, DateFormat.DMY_SPACE),
          testId: "breakdown-start-date",
        },
        {
          key: "end",
          label: "End date",
          value: formatDate(liability.endDate, DateFormat.DMY_SPACE),
          sub: "Debt-free",
          testId: "breakdown-end-date",
        },
      ];

      if (termInfo) {
        items.push({
          key: "term",
          label: "Term",
          value: termInfo.totalLabel,
          sub: termInfo.remainingLabel,
          testId: "breakdown-term",
        });
      }

      return items;
    }, [liability]);

    const interestPaid = formatCurrency(liability.interestAmount);
    const totalPaid = formatCurrency(liability.paidAmount);
    const repaidLabel = `${formatPercent(repaidPercent, 1)} repaid`;

    return (
      <BreakdownCard data-testid="loan-breakdown">
        <BreakdownHeader>
          <BreakdownTitle>Loan breakdown</BreakdownTitle>
          <RepaidTag data-testid="breakdown-repaid">{repaidLabel}</RepaidTag>
        </BreakdownHeader>

        <ProgressTrack
          role="progressbar"
          aria-valuenow={Math.round(repaidPercent)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <ProgressFill $pct={repaidPercent} />
        </ProgressTrack>

        <StatColumns>
          {stats.map((stat) => (
            <Fragment key={stat.key}>
              {stat.key === DIVIDER_BEFORE_KEY && <StatDivider aria-hidden />}
              <StatColumn>
                <StatColumnLabel>{stat.label}</StatColumnLabel>
                <StatColumnValueRow>
                  <Swatch $variant={stat.swatch} aria-hidden />
                  <StatColumnValue $tone={stat.tone}>
                    {stat.value}
                  </StatColumnValue>
                </StatColumnValueRow>
              </StatColumn>
            </Fragment>
          ))}
        </StatColumns>

        <StatList>
          {stats.map((stat) => (
            <StatRowItem key={stat.key}>
              <StatRowLeft>
                <Dot $variant={stat.swatch} aria-hidden />
                <StatRowLabel>{stat.shortLabel}</StatRowLabel>
              </StatRowLeft>
              <StatRowValue $tone={stat.tone}>{stat.value}</StatRowValue>
            </StatRowItem>
          ))}
        </StatList>

        <BreakdownNote>
          <NotePrefix>Cost of borrowing so far: </NotePrefix>
          {interestPaid} of the {totalPaid} you&apos;ve paid went to interest.
        </BreakdownNote>

        <DetailsDivider aria-hidden />

        <DetailRow>
          {details.map((detail) => (
            <DetailItem key={detail.key}>
              <DetailItemLabel>{detail.label}</DetailItemLabel>
              <DetailItemValue
                $accent={detail.accent}
                data-testid={detail.testId}
              >
                {detail.value}
              </DetailItemValue>
              {detail.sub && <DetailItemSub>{detail.sub}</DetailItemSub>}
            </DetailItem>
          ))}
        </DetailRow>
      </BreakdownCard>
    );
  },
);

LoanBreakdown.displayName = "LoanBreakdown";
